import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Card as CardMaterial,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  CardMedia,
  Tooltip,
  createTheme,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteJuntaDirectiva,
  getJuntaDirectivaById,
  postJuntaDirectiva,
  updateJuntaDirectiva,
} from "../../../../api/juntaDirectiva";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { Autocomplete } from "@material-ui/lab";
import { SERVER_URL } from "../../../../api";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import { Delete, Visibility } from "@material-ui/icons";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { getLiteralByNombre } from "../../../../api/literal";
import ImageStorageSelectorDialog from "../../../components/dialogs/ImageStorageSelectorDialog";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

// Mapeo entre claves del literal y valores del enum (que se guardan en BD)
const CATEGORIA_LITERAL_TO_ENUM = {
  presidencia: "Presidencia",
  vicepresidencia: "Vicepresidencia",
  secretarioGeneral: "Secretario general",
  relacionesInternacionales: "Relaciones internacionales",
  tesorero: "Tesorero",
  vocal: "Vocal",
  secretariaTecnica: "Secretaria técnica",
  medicoResponsable: "Médico responsable",
  webmaster: "Webmaster",
  miembroDeHonor: "Miembro de honor",
};

// Mapeo inverso: de valores del enum a claves del literal
const CATEGORIA_ENUM_TO_LITERAL = Object.fromEntries(
  Object.entries(CATEGORIA_LITERAL_TO_ENUM).map(([key, value]) => [value, key])
);

function getEmptyJuntaDirectiva() {
  return {
    nombre: "",
    apellidos: "",
    email: "",
    linkedin: "",
    twitter: "",
    categoria: "",
    foto: "",
    active: true,
  };
}

export default function EditJuntaDirectivaPage() {
  const [juntaDirectiva, setJuntaDirectiva] = useState(getEmptyJuntaDirectiva());
  const [categorias, setCategorias] = useState([]);
  const imageInputRef = useRef(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);
  const [openImageSelector, setOpenImageSelector] = useState(false);
  const juntaDirectivaId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  // Cargar categorías desde el literal
  useEffect(() => {
    const loadCategorias = async () => {
      try {
        const response = await getLiteralByNombre("pages.juntaDirectiva");
        if (response.status === 200 && response.data?.content?.categories) {
          const categoriesContent = response.data.content.categories;
          // Extraer las categorías en español y mapearlas a los valores del enum
          const categoriasArray = Object.keys(CATEGORIA_LITERAL_TO_ENUM)
            .map((key) => {
              const categoryValue = categoriesContent[key];
              if (categoryValue) {
                // Si es multilenguaje, tomar el valor en español
                const esValue = typeof categoryValue === 'object' && categoryValue.es 
                  ? categoryValue.es 
                  : typeof categoryValue === 'string' 
                  ? categoryValue 
                  : null;
                // Devolver el valor del enum (que es lo que se guarda en BD)
                return CATEGORIA_LITERAL_TO_ENUM[key];
              }
              return null;
            })
            .filter(Boolean);
          setCategorias(categoriasArray);
        } else {
          // Fallback a valores por defecto si no se puede cargar el literal
          setCategorias(Object.values(CATEGORIA_LITERAL_TO_ENUM));
        }
      } catch (error) {
        console.warn("Error cargando categorías desde literal, usando valores por defecto:", error);
        // Fallback a valores por defecto
        setCategorias(Object.values(CATEGORIA_LITERAL_TO_ENUM));
      }
    };
    loadCategorias();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      if (!juntaDirectivaId) {
        disableLoadingData();
        return;
      }
      getJuntaDirectivaById(juntaDirectivaId)
        .then((res) => {
          if (res.status === 200) {
            const juntaDirectiva = res.data;
            setJuntaDirectiva(juntaDirectiva);
            disableLoadingData();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se pudo obtener el miembro de la junta directiva.",
          });
          history.push("/junta-directiva");
        });
    };
    fetch();
  }, [juntaDirectivaId, disableLoadingData, history]);

  function saveJuntaDirectiva() {
    let saveJuntaDirectiva = juntaDirectiva;
    if (!juntaDirectivaId) {
      postJuntaDirectiva(saveJuntaDirectiva, selectedImage)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Guardado!",
              customMessage: "Miembro de la junta directiva creado correctamente.",
            });
            history.push("/junta-directiva");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se pudo guardar el miembro de la junta directiva.",
          });
        });
    } else {
      updateJuntaDirectiva(juntaDirectivaId, saveJuntaDirectiva, selectedImage)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Guardado!",
              customMessage: "Cambios guardados correctamente.",
            });
            history.push("/junta-directiva");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se pudieron guardar los cambios.",
          });
        });
    }
  }

  const handleChange = (element) => (event) => {
    setJuntaDirectiva({ ...juntaDirectiva, [element]: event.target.value });
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContentDatosGenerales = () => {
    return (
      <>
        <div className="d-flex align-text-center">
          <div className="col-md-6">
            <TextField
              id={`nombre`}
              label="Nombre"
              value={juntaDirectiva.nombre || ""}
              onChange={handleChange("nombre")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              required
              fullWidth
            />
          </div>
          <div className="col-md-6">
            <TextField
              id={`apellidos`}
              label="Apellidos"
              value={juntaDirectiva.apellidos || ""}
              onChange={handleChange("apellidos")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              fullWidth
            />
          </div>
        </div>
        <div className="d-flex align-text-center">
          <div className="col-md-6">
            <TextField
              id={`email`}
              label="Email"
              value={juntaDirectiva.email || ""}
              onChange={handleChange("email")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              type="email"
              fullWidth
            />
          </div>
          <div className="col-md-6">
            <Autocomplete
              options={categorias}
              value={juntaDirectiva.categoria || ""}
              onChange={(event, newValue) => {
                setJuntaDirectiva({ ...juntaDirectiva, categoria: newValue || "" });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categoría"
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              )}
            />
          </div>
        </div>
        <div className="d-flex align-text-center">
          <div className="col-md-6">
            <TextField
              id={`linkedin`}
              label="Usuario LinkedIn"
              value={juntaDirectiva.linkedin || ""}
              onChange={handleChange("linkedin")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              fullWidth
            />
          </div>
          <div className="col-md-6">
            <TextField
              id={`twitter`}
              label="Usuario X (Twitter)"
              value={juntaDirectiva.twitter || ""}
              onChange={handleChange("twitter")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              fullWidth
            />
          </div>
        </div>
        <div className="d-flex align-text-center">
          <div className="col-md-12">
            <CardMaterial>
              {(selectedImage || juntaDirectiva.foto) && (
                <CardMedia
                  component="img"
                  alt="Foto"
                  style={{
                    objectFit: "cover",
                    width: "200px",
                    height: "200px",
                    borderRadius: "50%",
                    margin: "20px auto",
                    display: "block",
                  }}
                  image={
                    selectedImage
                      ? URL.createObjectURL(selectedImage)
                      : `${SERVER_URL}/${juntaDirectiva.foto}`
                  }
                />
              )}
            </CardMaterial>
            <div style={{ display: "flex", gap: "10px", marginTop: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
              <label htmlFor={"upload-media"} className="ml-0">
                <input
                  ref={imageInputRef}
                  style={{ display: "none" }}
                  id={"upload-media"}
                  name={"upload-media"}
                  type="file"
                  accept={"image/*"}
                  onChange={(e) => {
                    setSelectedImage(e.target.files[0]);
                  }}
                />
                <Button
                  style={{ marginRight: "15px" }}
                  color="secondary"
                  component="span"
                  variant="outlined"
                >
                  {selectedImage ||
                  (juntaDirectiva.foto && juntaDirectiva.foto !== "")
                    ? "Cambiar fotografía"
                    : "Subir fotografía"}
                </Button>
              </label>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenImageSelector(true)}
              >
                Seleccionar de galería
              </Button>
            </div>
            {(selectedImage ||
              (juntaDirectiva.foto && juntaDirectiva.foto !== "")) && (
              <>
                <Tooltip title={"Preview"}>
                  <Button
                    size="small"
                    onClick={() => setOpenPreviewDialog(1)}
                    style={{
                      marginRight: "15px",
                    }}
                  >
                    <Visibility />
                  </Button>
                </Tooltip>
                <PreviewDialog
                  title={"Preview"}
                  open={openPreviewDialog === 1}
                  setOpen={setOpenPreviewDialog}
                  file={selectedImage}
                  src={
                    selectedImage
                      ? URL.createObjectURL(selectedImage)
                      : `${SERVER_URL}/${juntaDirectiva.foto}`
                  }
                />
                <Tooltip title={"Delete"}>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedImage(null);
                      setJuntaDirectiva({
                        ...juntaDirectiva,
                        foto: null,
                      });
                      if (imageInputRef.current) {
                        imageInputRef.current.value = "";
                      }
                    }}
                  >
                    <Delete />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/junta-directiva"),
            onSave: saveJuntaDirectiva,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!juntaDirectivaId,
            setObj: setJuntaDirectiva,
            obj: juntaDirectiva,
          })}
          <CardHeader title="Editar Junta Directiva"></CardHeader>
          <CardBody>
            {renderContentDatosGenerales()}

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={juntaDirectiva.active !== false}
                    onChange={() =>
                      setJuntaDirectiva({ ...juntaDirectiva, active: !juntaDirectiva.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
            </div>
          </CardBody>
        </Card>

        {/* Diálogo de selección de imageStorage */}
        <ImageStorageSelectorDialog
          open={openImageSelector}
          onClose={() => setOpenImageSelector(false)}
          onSelect={(url) => {
            setJuntaDirectiva({ ...juntaDirectiva, foto: url });
            setSelectedImage(null);
          }}
          filterType="imagen"
          title="Seleccionar fotografía"
        />

        {juntaDirectivaId && (
          <>
            <ConfirmDialog
              title={"¿Estás seguro de que quieres eliminar este miembro de la junta directiva?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteJuntaDirectiva(juntaDirectivaId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Eliminado!",
                        customMessage: "Miembro de la junta directiva eliminado correctamente",
                      });
                      history.push("/junta-directiva");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "No se pudo eliminar el miembro de la junta directiva.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}

